import { useMutation, useQueryClient } from '@tanstack/react-query';
import { seriesClient } from '@/api/client/series.client';
import { queryKeys } from '@/api/query-keys';
import type { BookmarkResponse, Series } from '@/types';

interface ToggleSeriesWatchlistVars {
  uuid: string;
  type: 'series' | 'course';
  /** Cache key the detail page used (series → seriesId, course → identifier). When
   *  supplied, the matching detail cache is optimistically toggled so the Save button
   *  flips instantly. Omit for non-detail consumers (e.g. home cards). */
  detailKey?: string | number;
}

interface ToggleContext {
  detailQueryKey?: readonly unknown[];
  previous?: Series;
}

function detailKeyFor(type: 'series' | 'course', key: string | number) {
  return type === 'course' ? queryKeys.courses.detail(key) : queryKeys.series.detail(key);
}

/**
 * Toggle save/watchlist on a series OR course. Both share the `/series-watchlist`
 * endpoint (courses are series rows with `module_type='course'`). Optimistically
 * flips `in_watchlist` on the relevant detail cache; rolls back on error.
 */
export function useToggleSeriesWatchlist() {
  const queryClient = useQueryClient();

  return useMutation<BookmarkResponse, Error, ToggleSeriesWatchlistVars, ToggleContext>({
    mutationFn: ({ uuid }) => seriesClient.toggleWatchlist(uuid),

    onMutate: async ({ type, detailKey }) => {
      if (detailKey === undefined) return {};

      const detailQueryKey = detailKeyFor(type, detailKey);
      await queryClient.cancelQueries({ queryKey: detailQueryKey });

      const previous = queryClient.getQueryData<Series>(detailQueryKey);
      if (previous) {
        queryClient.setQueryData<Series>(detailQueryKey, {
          ...previous,
          in_watchlist: !previous.in_watchlist,
        });
      }

      return { detailQueryKey, ...(previous !== undefined ? { previous } : {}) };
    },

    onSuccess: (data, _vars, context) => {
      if (!context?.detailQueryKey) return;
      const cached = queryClient.getQueryData<Series>(context.detailQueryKey);
      if (cached) {
        queryClient.setQueryData<Series>(context.detailQueryKey, {
          ...cached,
          in_watchlist: data.in_watchlist,
        });
      }
    },

    onError: (_err, _vars, context) => {
      if (context?.detailQueryKey && context.previous) {
        queryClient.setQueryData(context.detailQueryKey, context.previous);
      }
    },

    onSettled: (_data, _err, { type, detailKey }) => {
      if (detailKey !== undefined) {
        queryClient.invalidateQueries({ queryKey: detailKeyFor(type, detailKey) });
        if (type === 'course') {
          queryClient.invalidateQueries({ queryKey: queryKeys.publicCourses.detail(detailKey) });
        }
      }
      // Prefix covers every list variant: `watchlist()` alone resolves to the 'all' bucket and
      // misses the per-type series/course lists `/watchlist` renders.
      queryClient.invalidateQueries({ queryKey: [...queryKeys.series.all, 'watchlist'] });
    },
  });
}
